// maths.portll.net — warm design-system kit.
//
// Small, dependency-free primitives built on the CSS tokens in styles/global.css
// (--ink, --paper, --card, --brand, --accent, --sky, --danger, --radius, --shadow).
// Tone is warm + Australian + NON-school-official: never "assessment / score / grade".
// Everything is iPad-safe: no hover-only affordances, tap targets ≥ 44px, and the
// expandable curriculum tag is a zero-JS <details> so it works under touch + keyboard.
//
// Styles are injected ONCE per document (guarded by element id) so these components
// need no external stylesheet and can be imported anywhere, print sheets included.

import { Link } from "react-router-dom";
import { useThemePref } from "../lib/theme.js";

/** Inject a <style> block once (idempotent, HMR- and SSR-safe). Shared helper. */
export function injectStyles(id, css) {
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;
  const el = document.createElement("style");
  el.id = id;
  el.textContent = css;
  document.head.appendChild(el);
}

/** Global light/dark/system toggle — a compact floating pill wired to the shared theme hook.
 *  Rendered once app-wide (App.jsx) so the flag is reachable on EVERY page + the game. */
const THEME_NEXT = { system: "light", light: "dark", dark: "system" };
const THEME_ICON = { system: "🖥️", light: "☀️", dark: "🌙" };
export function ThemeToggle() {
  const [choice, setChoice] = useThemePref();
  injectStyles(
    "mp-theme-toggle-css",
    `.mp-theme-toggle{position:fixed;right:14px;bottom:14px;z-index:60;width:42px;height:42px;
      border-radius:999px;display:inline-flex;align-items:center;justify-content:center;font-size:19px;line-height:1;
      background:var(--mp-surface-container,#232733);color:var(--mp-on-surface,#eceef3);
      border:1px solid var(--mp-outline-variant,#3a3f4e);box-shadow:var(--shadow);cursor:pointer;padding:0;}
    .mp-theme-toggle:hover{background:var(--mp-surface-high,#2a2f3d);}
    @media print{.mp-theme-toggle{display:none;}}`
  );
  return (
    <button
      type="button"
      className="mp-theme-toggle no-print"
      onClick={() => setChoice(THEME_NEXT[choice] || "system")}
      aria-label={`Colour theme: ${choice}. Tap to switch.`}
      title={`Theme: ${choice} (tap to switch)`}
    >
      <span aria-hidden="true">{THEME_ICON[choice] || "🖥️"}</span>
    </button>
  );
}

/** Strand → warm label + chip tone + emoji. Exported so pages can reuse the mapping. */
export const STRAND_META = {
  number: { label: "Number", tone: "brand", emoji: "🔢" },
  algebra: { label: "Algebra", tone: "sky", emoji: "🧩" },
  measurement: { label: "Measurement", tone: "accent", emoji: "📏" },
  space: { label: "Space", tone: "sky", emoji: "🧭" },
  statistics: { label: "Statistics", tone: "brand", emoji: "📊" },
  probability: { label: "Probability", tone: "accent", emoji: "🎲" },
};

const cx = (...parts) => parts.filter(Boolean).join(" ");

// ── PageHeader ────────────────────────────────────────────────────────────────
function BackControl({ back }) {
  const label = (
    <>
      <span aria-hidden="true">←</span> Back
    </>
  );
  if (typeof back === "function")
    return (
      <button type="button" className="ui-back" onClick={back}>
        {label}
      </button>
    );
  if (back === true)
    return (
      <button type="button" className="ui-back" onClick={() => window.history.back()}>
        {label}
      </button>
    );
  // string path → in-app link
  return (
    <Link to={back} className="ui-back">
      {label}
    </Link>
  );
}

export function PageHeader({ title, subtitle, back, children }) {
  return (
    <header className="ui-pageheader">
      {back != null && back !== false && <BackControl back={back} />}
      <div className="ui-pageheader__body">
        <h1 className="ui-pageheader__title">{title}</h1>
        {subtitle && <p className="ui-pageheader__sub">{subtitle}</p>}
      </div>
      {children != null && <div className="ui-pageheader__aside">{children}</div>}
    </header>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ as: As = "div", className = "", children, ...rest }) {
  return (
    <As className={cx("card", "ui-card", className)} {...rest}>
      {children}
    </As>
  );
}

// ── Chip ──────────────────────────────────────────────────────────────────────
export function Chip({ tone = "neutral", className = "", children, ...rest }) {
  return (
    <span className={cx("ui-chip", `ui-chip--${tone}`, className)} {...rest}>
      {children}
    </span>
  );
}

// ── Button (styled; renders <button> or a router <Link> when `to` is set) ──────
export function Button({
  variant = "primary",
  size = "md",
  to,
  type = "button",
  className = "",
  children,
  ...rest
}) {
  const cls = cx("ui-btn", `ui-btn--${variant}`, `ui-btn--${size}`, className);
  if (to != null)
    return (
      <Link to={to} className={cls} {...rest}>
        {children}
      </Link>
    );
  return (
    <button type={type} className={cls} {...rest}>
      {children}
    </button>
  );
}

// ── Field (label + control wrapper) ────────────────────────────────────────────
export function Field({ label, hint, htmlFor, className = "", children }) {
  return (
    <div className={cx("ui-field", className)}>
      {label && (
        <label className="ui-field__label" htmlFor={htmlFor}>
          {label}
        </label>
      )}
      {children}
      {hint && <p className="ui-field__hint">{hint}</p>}
    </div>
  );
}

// ── EmptyState ─────────────────────────────────────────────────────────────────
export function EmptyState({ title, hint, icon = "🌱", action }) {
  return (
    <div className="ui-empty">
      <div className="ui-empty__icon" aria-hidden="true">
        {icon}
      </div>
      {title && <h3 className="ui-empty__title">{title}</h3>}
      {hint && <p className="ui-empty__hint">{hint}</p>}
      {action && <div className="ui-empty__action">{action}</div>}
    </div>
  );
}

// ── Spinner ────────────────────────────────────────────────────────────────────
export function Spinner({ size = 22, label = "Loading" }) {
  return (
    <span
      className="ui-spinner"
      role="status"
      aria-label={label}
      style={{ width: size, height: size, borderWidth: Math.max(2, Math.round(size / 10)) }}
    />
  );
}

// ── Note (soft, never-blocking inline message) ────────────────────────────────
export function Note({ tone = "neutral", className = "", children }) {
  return <p className={cx("ui-note", `ui-note--${tone}`, className)}>{children}</p>;
}

// ── StrandBadge ────────────────────────────────────────────────────────────────
export function StrandBadge({ strand }) {
  const m = STRAND_META[strand] || { label: strand || "Maths", tone: "neutral", emoji: "•" };
  return (
    <Chip tone={m.tone}>
      <span aria-hidden="true">{m.emoji}</span>
      {m.label}
    </Chip>
  );
}

// ── CurriculumTag (expandable ACARA codes; zero-JS <details>) ──────────────────
export function CurriculumTag({ codes, label = "Curriculum" }) {
  const list = Array.isArray(codes) ? codes.filter(Boolean) : [];
  if (!list.length) return null;
  return (
    <details className="ui-curric">
      <summary className="ui-curric__summary">
        <span aria-hidden="true">📚</span>
        <span>{label}</span>
        <span className="ui-curric__count">{list.length}</span>
      </summary>
      <ul className="ui-curric__list">
        {list.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ul>
    </details>
  );
}

// ── styles ─────────────────────────────────────────────────────────────────────
const UI_CSS = `
.ui-pageheader{display:flex;flex-wrap:wrap;align-items:flex-start;gap:.5rem .9rem;margin:0 0 1.25rem;}
.ui-pageheader__body{flex:1 1 12rem;min-width:0;}
.ui-pageheader__title{margin:.1rem 0 0;font-size:clamp(1.4rem,4.5vw,1.9rem);}
.ui-pageheader__sub{margin:.3rem 0 0;color:var(--ink-soft);}
.ui-pageheader__aside{flex:0 0 auto;display:flex;gap:.5rem;flex-wrap:wrap;align-items:center;}
.ui-back{display:inline-flex;align-items:center;gap:.35rem;min-height:44px;padding:.4rem .85rem;
  background:var(--card);color:var(--brand-ink);border:1px solid var(--line);border-radius:999px;
  font-weight:600;text-decoration:none;line-height:1;}
.ui-back:active{transform:translateY(1px);}

.ui-card{padding:1rem 1.25rem;}

.ui-chip{display:inline-flex;align-items:center;gap:.35em;font-size:.8rem;font-weight:600;
  padding:.28em .62em;border-radius:999px;border:1px solid transparent;line-height:1.2;white-space:nowrap;}
.ui-chip--neutral{color:var(--ink-soft);background:color-mix(in srgb,var(--ink-soft) 8%,#fff);border-color:var(--line);}
.ui-chip--brand{color:var(--brand-ink);background:color-mix(in srgb,var(--brand) 12%,#fff);border-color:color-mix(in srgb,var(--brand) 28%,#fff);}
.ui-chip--accent{color:#7a4a10;background:color-mix(in srgb,var(--accent) 16%,#fff);border-color:color-mix(in srgb,var(--accent) 34%,#fff);}
.ui-chip--sky{color:#1e4d63;background:color-mix(in srgb,var(--sky) 14%,#fff);border-color:color-mix(in srgb,var(--sky) 32%,#fff);}
.ui-chip--danger{color:var(--danger);background:color-mix(in srgb,var(--danger) 10%,#fff);border-color:color-mix(in srgb,var(--danger) 28%,#fff);}
.ui-chip--ok{color:var(--brand-ink);background:color-mix(in srgb,var(--brand) 14%,#fff);border-color:color-mix(in srgb,var(--brand) 30%,#fff);}

.ui-btn{display:inline-flex;align-items:center;justify-content:center;gap:.45rem;font-weight:600;
  text-decoration:none;border:1px solid transparent;border-radius:12px;line-height:1;cursor:pointer;
  transition:transform .04s ease;}
.ui-btn:active{transform:translateY(1px);}
.ui-btn:disabled,.ui-btn[aria-disabled="true"]{opacity:.5;cursor:default;transform:none;}
.ui-btn--sm{min-height:44px;padding:.45rem .8rem;font-size:.92rem;}
.ui-btn--md{min-height:48px;padding:.6rem 1.1rem;}
.ui-btn--lg{min-height:56px;padding:.85rem 1.5rem;font-size:1.08rem;}
.ui-btn--primary{background:var(--brand);color:#fff;}
.ui-btn--secondary{background:var(--card);color:var(--brand-ink);border-color:var(--line);}
.ui-btn--ghost{background:transparent;color:var(--brand-ink);}
.ui-btn--danger{background:var(--danger);color:#fff;}
.ui-btn--accent{background:var(--accent);color:#fff;}

.ui-field{display:flex;flex-direction:column;gap:.35rem;margin:0 0 .9rem;}
.ui-field__label{font-weight:600;color:var(--brand-ink);font-size:.95rem;}
.ui-field__hint{margin:.1rem 0 0;color:var(--ink-soft);font-size:.85rem;}
.ui-field :is(input,textarea,select){font:inherit;color:var(--ink);background:var(--card);
  border:1px solid var(--line);border-radius:10px;padding:.6rem .75rem;min-height:44px;width:100%;}
.ui-field :is(input,textarea,select):focus-visible{outline:2px solid var(--sky);outline-offset:1px;}

.ui-empty{text-align:center;padding:2rem 1rem;color:var(--ink-soft);}
.ui-empty__icon{font-size:2.2rem;line-height:1;}
.ui-empty__title{margin:.6rem 0 .2rem;color:var(--brand-ink);}
.ui-empty__hint{margin:0 auto;max-width:34ch;}
.ui-empty__action{margin-top:1rem;}

.ui-spinner{display:inline-block;border-style:solid;border-color:color-mix(in srgb,var(--brand) 25%,#fff);
  border-top-color:var(--brand);border-radius:50%;animation:ui-spin .7s linear infinite;box-sizing:border-box;}
@keyframes ui-spin{to{transform:rotate(360deg);}}
@media (prefers-reduced-motion:reduce){.ui-spinner{animation-duration:1.6s;}}

.ui-note{margin:.5rem 0;padding:.55rem .75rem;border-radius:10px;font-size:.9rem;border:1px solid transparent;}
.ui-note--neutral{color:var(--ink-soft);background:color-mix(in srgb,var(--ink-soft) 6%,#fff);border-color:var(--line);}
.ui-note--brand,.ui-note--ok{color:var(--brand-ink);background:color-mix(in srgb,var(--brand) 8%,#fff);border-color:color-mix(in srgb,var(--brand) 24%,#fff);}
.ui-note--warn,.ui-note--accent{color:#7a4a10;background:color-mix(in srgb,var(--accent) 12%,#fff);border-color:color-mix(in srgb,var(--accent) 30%,#fff);}
.ui-note--danger{color:var(--danger);background:color-mix(in srgb,var(--danger) 8%,#fff);border-color:color-mix(in srgb,var(--danger) 26%,#fff);}
.ui-note--sky{color:#1e4d63;background:color-mix(in srgb,var(--sky) 10%,#fff);border-color:color-mix(in srgb,var(--sky) 28%,#fff);}

.ui-curric{display:inline-block;border:1px solid var(--line);border-radius:999px;background:var(--card);}
.ui-curric[open]{border-radius:14px;}
.ui-curric__summary{display:inline-flex;align-items:center;gap:.4rem;min-height:44px;padding:.3rem .8rem;
  list-style:none;cursor:pointer;font-weight:600;font-size:.82rem;color:var(--brand-ink);}
.ui-curric__summary::-webkit-details-marker{display:none;}
.ui-curric__count{display:inline-grid;place-items:center;min-width:1.4em;height:1.4em;padding:0 .3em;
  border-radius:999px;background:color-mix(in srgb,var(--brand) 14%,#fff);color:var(--brand-ink);font-size:.78rem;}
.ui-curric__list{list-style:none;margin:0;padding:.2rem .8rem .7rem;display:flex;flex-wrap:wrap;gap:.35rem;}
.ui-curric__list code{font-size:.78rem;background:color-mix(in srgb,var(--sky) 8%,#fff);color:#1e4d63;
  border:1px solid color-mix(in srgb,var(--sky) 24%,#fff);border-radius:6px;padding:.15rem .4rem;}
`;

injectStyles("maths-ui-styles", UI_CSS);
